import { useNavigate } from '@tanstack/react-router';

interface StoredCheckInResult {
  id?: string;
  eventId?: string;
  userId?: string;
  userName?: string;
  checkedInAt?: string;
  alreadyCheckedIn?: boolean;
  error?: boolean;
  message?: string;
}

type ResultStatus = 'success' | 'already-checked-in' | 'error';

/**
 * Seite zur Anzeige des Check-in Ergebnisses.
 * Liest das Ergebnis aus sessionStorage und zeigt Erfolg, Duplikat oder Fehler an.
 *
 * @returns Ergebnis-Seite mit Scan-Weiter-Button
 */
export function ScanResultPage() {
  const navigate = useNavigate();

  const rawResult = sessionStorage.getItem('lastCheckInResult');
  const result: StoredCheckInResult = rawResult
    ? (JSON.parse(rawResult) as StoredCheckInResult)
    : {};

  let status: ResultStatus;
  if (result.error) {
    status = 'error';
  } else if (result.alreadyCheckedIn) {
    status = 'already-checked-in';
  } else {
    status = 'success';
  }

  const statusConfig = {
    success: {
      icon: 'OK',
      color: '#4ade80',
      backgroundColor: '#0d2b1a',
      border: '#166534',
      title: 'Erfolgreich eingecheckt!',
    },
    'already-checked-in': {
      icon: '!',
      color: '#fbbf24',
      backgroundColor: '#2b1f0d',
      border: '#92400e',
      title: 'Bereits eingecheckt',
    },
    error: {
      icon: 'X',
      color: '#f87171',
      backgroundColor: '#2d1515',
      border: '#991b1b',
      title: 'Check-in fehlgeschlagen',
    },
  }[status];

  const handleScanNext = () => {
    sessionStorage.removeItem('lastCheckInResult');
    void navigate({ to: '/' });
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: '#0f0f23',
        color: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        fontFamily: 'system-ui, sans-serif',
        gap: '24px',
      }}
    >
      {/* Status Card */}
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: statusConfig.backgroundColor,
          border: `2px solid ${statusConfig.border}`,
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            backgroundColor: statusConfig.border,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            fontWeight: 'bold',
            color: statusConfig.color,
          }}
        >
          {statusConfig.icon}
        </div>

        <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0, color: statusConfig.color }}>
          {statusConfig.title}
        </h1>

        {/* User Info */}
        {result.userName && (
          <p style={{ fontSize: '18px', margin: 0, color: '#ffffff' }}>{result.userName}</p>
        )}

        {result.userId && !result.userName && (
          <p style={{ fontSize: '14px', margin: 0, color: '#aaa' }}>ID: {result.userId}</p>
        )}

        {/* Timestamp */}
        {result.checkedInAt && (
          <p style={{ fontSize: '13px', margin: 0, color: '#aaa' }}>
            {new Date(result.checkedInAt).toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}{' '}
            Uhr
          </p>
        )}

        {/* Error Message */}
        {status === 'error' && result.message && (
          <p style={{ fontSize: '14px', margin: 0, color: '#f87171' }}>{result.message}</p>
        )}

        {/* Already checked in hint */}
        {status === 'already-checked-in' && (
          <p style={{ fontSize: '14px', margin: 0, color: '#fbbf24' }}>
            Diese Person wurde bereits eingecheckt.
          </p>
        )}
      </div>

      {/* Scan Next Button */}
      <button
        onClick={handleScanNext}
        style={{
          minHeight: '56px',
          minWidth: '200px',
          padding: '16px 32px',
          fontSize: '18px',
          fontWeight: 'bold',
          backgroundColor: '#4ade80',
          color: '#0f0f23',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          touchAction: 'manipulation',
        }}
      >
        Nächsten scannen
      </button>
    </div>
  );
}
